"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CheckCircle, Lock, X } from "lucide-react";
import { useEffect, useState } from "react";
interface OnboardingMilestoneModalProps {
  open: boolean;
  onClose: () => void;
  canClaimReward?: boolean;
  checkKycStatus?: () => boolean;
  claimOnboardingReward?: () => Promise<boolean>;
}

export function OnboardingMilestoneModal({
  open,
  onClose,
  checkKycStatus,
  claimOnboardingReward,
}: OnboardingMilestoneModalProps) {
  const [isKycApproved, setIsKycApproved] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);

  useEffect(() => {
    if (open && checkKycStatus) {
      setIsKycApproved(checkKycStatus());
    }
  }, [open, checkKycStatus]);

  const handleClaimReward = async () => {
    if (!claimOnboardingReward) return;

    setIsClaiming(true);
    try {
      await claimOnboardingReward();
      onClose();
    } catch (error) {
    } finally {
      setIsClaiming(false);
    }
  };

  if (isKycApproved) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md bg-black/90 backdrop-blur-md border border-white/20 text-white">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>

          <DialogHeader className="text-center space-y-6 pt-2">
            <DialogTitle className="text-xl font-semibold text-white">
              Ready to Claim!
            </DialogTitle>

            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="h-8 w-8 text-green-400" />
              <span className="text-3xl font-bold text-green-400">
                9.26 IMX
              </span>
            </div>

            <p className="text-sm text-gray-300 leading-relaxed px-2">
              Your KYC verification is complete! Click below to claim your
              onboarding reward.
            </p>
          </DialogHeader>

          <div className="flex flex-col gap-3 mt-6">
            <Button
              onClick={handleClaimReward}
              disabled={isClaiming}
              className="w-full bg-green-500 hover:bg-green-600 disabled:bg-green-500/50 text-white font-medium py-3 rounded-lg transition-colors"
            >
              {isClaiming ? (
                "Claiming..."
              ) : (
                <span className="bg-gradient-text bg-clip-text text-transparent font-bold text-lg tracking-wider">
                  CLAIM NOW
                </span>
              )}
            </Button>

            <Button
              onClick={onClose}
              variant="ghost"
              className="w-full text-gray-400 hover:text-white hover:bg-white/10 py-3 rounded-lg transition-colors"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-black/90 backdrop-blur-md border border-white/20 text-white">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        <DialogHeader className="text-center space-y-6 pt-2">
          <DialogTitle className="text-xl font-semibold text-white">
            Claim Your Prize
          </DialogTitle>

          <div className="flex items-center justify-center gap-2">
            <span className="text-3xl font-bold text-cyan-400">9.26 IMX</span>
            <Lock className="h-5 w-5 text-gray-400" />
          </div>

          <p className="text-sm text-gray-300 leading-relaxed px-2">
            Please KYC to receive your $5 USD prize paid in IMX. Click the link
            below and upload your documents. Once approved, you can claim your
            reward.*
          </p>
        </DialogHeader>

        <div className="flex flex-col gap-3 mt-6">
          <Button
            onClick={() =>
              window.open("https://docsend.com/view/gvyycnper74m43aq", "_blank")
            }
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-medium py-3 rounded-lg transition-colors"
            leftIcon={
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <title>External link icon</title>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            }
          >
            Complete KYC Verification
          </Button>

          <Button
            onClick={onClose}
            variant="ghost"
            className="w-full text-gray-400 hover:text-white hover:bg-white/10 py-3 rounded-lg transition-colors"
          >
            Cancel
          </Button>
        </div>

        <p className="text-xs text-gray-500 text-center mt-4">
          *There is a one time KYC fee of $3. KYC approval can take up to 30
          days.
        </p>
      </DialogContent>
    </Dialog>
  );
}
